import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Heart, ArrowRight } from "lucide-react";
import { api, formatGBP } from "../lib/api";

const MAX_POLLS = 8;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const campaignId = params.get("campaign_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ kind: "checking", attempts: 0 });
  const [result, setResult] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "error", attempts: 0 });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (cancelled.current) return;
      attempts++;
      setState({ kind: "checking", attempts });
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setResult(data);
        if (data.payment_status === "paid") {
          setState({ kind: "success", attempts });
          return;
        }
        if (data.status === "expired") {
          setState({ kind: "expired", attempts });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ kind: "timeout", attempts });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= MAX_POLLS) {
          setState({ kind: "error", attempts });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled.current = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-20">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                  className="premium-card p-12 sm:p-16 max-w-xl text-center">
        {state.kind === "checking" && (
          <>
            <Loader2 className="mx-auto text-brand-primary animate-spin" size={36} strokeWidth={1.4} />
            <h1 className="font-serif-display text-3xl mt-5">Confirming your donation…</h1>
            <p className="text-brand-muted mt-2">Attempt {state.attempts} of {MAX_POLLS}. Don't refresh the page.</p>
          </>
        )}

        {state.kind === "success" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-brand-primary-light flex items-center justify-center mb-4">
              <Heart className="text-brand-primary" size={28} strokeWidth={1.6} fill="currentColor" />
            </div>
            <h1 className="font-serif-display text-4xl">Thank you ✨</h1>
            <p className="text-brand-muted mt-3 leading-relaxed">
              Your gift of <span className="text-brand-primary font-medium">{formatGBP(result?.amount)}</span> has been received.
              You've just made someone's tomorrow brighter.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {campaignId && (
                <Link to={`/campaigns/${campaignId}`} className="btn-outline-gold !py-2.5 text-sm">Back to campaign</Link>
              )}
              <Link to="/campaigns" className="btn-gold !py-2.5 text-sm">
                Support another cause <ArrowRight size={14} className="ml-2" strokeWidth={1.8} />
              </Link>
            </div>
          </>
        )}

        {(state.kind === "expired" || state.kind === "error" || state.kind === "timeout") && (
          <>
            <XCircle className="mx-auto text-red-500" size={36} strokeWidth={1.4} />
            <h1 className="font-serif-display text-3xl mt-4">
              {state.kind === "expired" ? "Session expired" : "Couldn't confirm payment"}
            </h1>
            <p className="text-brand-muted mt-3">
              {state.kind === "timeout"
                ? "We are still processing. Please check your dashboard in a few minutes."
                : "If you completed payment, you'll see it in your dashboard shortly."}
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => navigate("/dashboard")} className="btn-outline-gold !py-2.5 text-sm">View dashboard</button>
              {campaignId && (
                <Link to={`/campaigns/${campaignId}`} className="btn-gold !py-2.5 text-sm">Try again</Link>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
